import React, { useEffect, useState } from 'react';
import TradeSymbolInput from './TradeSymbolInput';
import { getTradeContractMultiplier } from '../pages/tradeTableUtils';

export default function TradeEntryModal({
  show,
  title,
  form,
  accounts,
  symbols,
  selectedAccountLabel,
  onSelectedAccountLabelChange,
  onManageAccounts,
  onFieldChange,
  manualInputMode,
  onManualInputModeChange,
  derivedQuantity,
  onSubmit,
  onClose,
  onCancelEdit,
  canSubmit,
  saving,
  isViewingAnotherUser,
  editingTradeId,
}) {
  const [optionMode, setOptionMode] = useState(false);
  useEffect(() => {
    setOptionMode(false);
  }, [show, editingTradeId]);
  const isOption = optionMode || getTradeContractMultiplier(form.symbol) === 100;
  const amountMode = !isOption && manualInputMode === 'amount';
  if (!show) {
    return null;
  }

  return (
    <>
      <div className="modal-backdrop fade show" onClick={onClose} />
      <div
        className="modal fade show d-block"
        tabIndex="-1"
        role="dialog"
        aria-modal="true"
        onClick={(e) => { if (e.target === e.currentTarget) onClose(); }}
      >
        <div className="modal-dialog modal-lg modal-dialog-scrollable" role="document">
          <div className="modal-content">
            <div className="modal-header">
              <h5 className="modal-title">{title}</h5>
              <button
                type="button"
                className="btn-close"
                onClick={onClose}
                aria-label="Close"
              />
            </div>
            <div className="modal-body">
              <form onSubmit={onSubmit}>
                <TradeSymbolInput
                  key={editingTradeId || 'new'}
                  symbol={form.symbol}
                  symbols={symbols}
                  onChange={(value) => onFieldChange('symbol', value)}
                  onModeChange={(mode) => {
                    setOptionMode(mode === 'option');
                    if (mode === 'option') onManualInputModeChange('quantity');
                  }}
                  disabled={saving || isViewingAnotherUser}
                />

                <div className="mb-3">
                  <label htmlFor="trade-action" className="form-label fw-semibold">Action</label>
                  <select
                    id="trade-action"
                    className="form-select"
                    value={form.action}
                    onChange={(e) => onFieldChange('action', e.target.value)}
                    disabled={saving || isViewingAnotherUser}
                  >
                    <option value="BUY">BUY</option>
                    <option value="SELL">SELL</option>
                    <option value="DIVIDEND_REINVESTMENT">DIVIDEND_REINVESTMENT</option>
                  </select>
                </div>

                <div className="mb-3">
                  <label htmlFor="trade-saved-account" className="form-label fw-semibold">Saved Account</label>
                  <div className="input-group">
                    <select
                      id="trade-saved-account"
                      className="form-select"
                      value={selectedAccountLabel}
                      onChange={(e) => onSelectedAccountLabelChange(e.target.value)}
                      disabled={saving || isViewingAnotherUser || accounts.length === 0}
                      required
                    >
                      <option value="" disabled>
                        {accounts.length === 0 ? 'No saved accounts yet' : 'Select an account'}
                      </option>
                      {accounts.map((account) => (
                        <option key={account.display_label} value={account.display_label}>
                          {account.display_label}
                        </option>
                      ))}
                    </select>
                    <button
                      type="button"
                      className="btn btn-outline-secondary"
                      onClick={onManageAccounts}
                      disabled={saving || isViewingAnotherUser}
                      title="Add, edit, or delete saved accounts"
                      aria-label="Manage saved accounts"
                    >
                      <i className="bi bi-gear"></i>
                    </button>
                  </div>
                  <div className="form-text">
                    Use the gear to add, edit, or delete saved accounts.
                  </div>
                </div>

                <div className="row g-2 mb-3">
                  <div className="col-12">
                    <label className="form-label fw-semibold">Entry Mode</label>
                    {isOption ? (
                      <div className="form-control bg-body-tertiary">Contracts</div>
                    ) : (
                      <div className="btn-group w-100" role="group" aria-label="Trade entry mode">
                        <button
                          type="button"
                          className={`btn ${manualInputMode === 'quantity' ? 'btn-primary' : 'btn-outline-primary'}`}
                          onClick={() => onManualInputModeChange('quantity')}
                          disabled={saving || isViewingAnotherUser}
                        >
                          Shares
                        </button>
                        <button
                          type="button"
                          className={`btn ${manualInputMode === 'amount' ? 'btn-primary' : 'btn-outline-primary'}`}
                          onClick={() => onManualInputModeChange('amount')}
                          disabled={saving || isViewingAnotherUser}
                        >
                          Amount
                        </button>
                      </div>
                    )}
                    <div className="form-text">
                      {isOption
                        ? 'Enter the number of contracts and the quoted per-share premium. Notional = contracts x price x 100.'
                        : 'Use Shares when you know quantity, or Amount to derive quantity from price.'}
                    </div>
                  </div>
                  <div className="col-6">
                    <label htmlFor="trade-quantity-amount" className="form-label fw-semibold">
                      {amountMode
                        ? (form.action === 'SELL' ? 'Amount Sold' : 'Amount Spent')
                        : (isOption ? 'Contracts' : 'Quantity')}
                    </label>
                    <input
                      id="trade-quantity-amount"
                      type="number"
                      className="form-control"
                      min="0"
                      step="0.000001"
                      value={amountMode ? form.amount : form.quantity}
                      onChange={(e) => onFieldChange(
                        amountMode ? 'amount' : 'quantity',
                        e.target.value,
                      )}
                      disabled={saving}
                      readOnly={isViewingAnotherUser}
                      required
                    />
                  </div>
                  <div className="col-6">
                    <label htmlFor="trade-price" className="form-label fw-semibold">Price</label>
                    <input
                      id="trade-price"
                      type="number"
                      className="form-control"
                      min="0"
                      step="0.000001"
                      value={form.price}
                      onChange={(e) => onFieldChange('price', e.target.value)}
                      disabled={saving}
                      readOnly={isViewingAnotherUser}
                      required
                    />
                  </div>
                  {amountMode && (
                    <div className="col-12">
                      <div className="small text-muted">
                        Calculated Quantity: <strong>{Number(derivedQuantity || 0).toLocaleString('en-US', {
                          minimumFractionDigits: 0,
                          maximumFractionDigits: 6,
                        })}</strong>
                      </div>
                    </div>
                  )}
                </div>

                <div className="mb-3">
                  <label htmlFor="trade-datetime" className="form-label fw-semibold">Trade Date/Time</label>
                  <input
                    id="trade-datetime"
                    type="datetime-local"
                    className="form-control"
                    value={form.tradeDatetime}
                    onChange={(e) => onFieldChange('tradeDatetime', e.target.value)}
                    disabled={saving}
                    readOnly={isViewingAnotherUser}
                    required
                  />
                </div>

                <button
                  type="submit"
                  className="btn btn-success w-100"
                  disabled={!canSubmit || isViewingAnotherUser}
                >
                  {saving ? (
                    <>
                      <span className="spinner-border spinner-border-sm me-2"></span>
                      Saving...
                    </>
                  ) : (
                    <>
                      <i className={`bi ${editingTradeId ? 'bi-save' : 'bi-plus-circle'} me-2`}></i>
                      {editingTradeId ? 'Save Trade' : 'Add Trade'}
                    </>
                  )}
                </button>
                {editingTradeId && (
                  <button
                    type="button"
                    className="btn btn-outline-secondary w-100 mt-2"
                    onClick={onCancelEdit}
                    disabled={saving}
                  >
                    Cancel Edit
                  </button>
                )}
              </form>
            </div>
          </div>
        </div>
      </div>
    </>
  );
}